"use client";

import { useState, useMemo } from "react";
import Link from "next/link";
import Icon from "@/components/ui/AppIcon";
import type { VmConfiguratorOptions } from "@/lib/siteContent";
import { HOURS_PER_MONTH, formatSom } from "@/lib/panelPrices";
import type { PageTexts } from "@/lib/siteTexts";

interface OSTemplate {
  id: string;
  name: string;
  icon: string;
  color: string;
}

interface CPUOption {
  id: string;
  name: string;
  min_cores: number;
  max_cores: number;
  step_cores: number;
  price_per_core: number;
  description: string | null;
}

interface RAMOption {
  id: string;
  name: string;
  ram_type: string;
  min_gb: number;
  max_gb: number;
  step_gb: number;
  price_per_gb: number;
  description: string | null;
}

interface StorageOption {
  id: string;
  storage_type: string;
  name: string;
  description: string | null;
  min_size_gb: number;
  max_size_gb: number;
  step_size_gb: number;
  price_per_gb: number;
}

interface NetworkOption {
  id: string;
  name: string;
  bandwidth_gbps: number | null;
  price_per_tb: number;
  description: string | null;
}

// Wording comes from Admin -> Тексты страниц (virtual-machines.configurator.*).
export default function VMConfigurator({
  options,
  texts: t,
}: {
  options: VmConfiguratorOptions;
  texts: PageTexts<"virtual-machines">;
}) {
  const osTemplates = options.osTemplates as OSTemplate[];
  const cpuOptions = options.cpuOptions as CPUOption[];
  const ramOptions = options.ramOptions as RAMOption[];
  const storageOptions = options.storageOptions as StorageOption[];
  const networkOptions = options.networkOptions as NetworkOption[];
  const error = options.error;
  const trafficPrice = options.trafficPricePerGb;

  // Start from the first option of each kind
  const [cpu, setCpu] = useState(cpuOptions[0]?.min_cores ?? 2);
  const [ram, setRam] = useState(ramOptions[0]?.min_gb ?? 4);
  const [disk, setDisk] = useState(storageOptions[0]?.min_size_gb ?? 80);
  const [selectedOS, setSelectedOS] = useState(0);
  const [billing, setBilling] = useState<"hourly" | "monthly">("monthly");

  // Selected options (first active option by default)
  const [selectedCPUOption, setSelectedCPUOption] = useState<CPUOption | null>(cpuOptions[0] ?? null);
  const [selectedRAMOption, setSelectedRAMOption] = useState<RAMOption | null>(ramOptions[0] ?? null);
  const [selectedStorageOption, setSelectedStorageOption] = useState<StorageOption | null>(storageOptions[0] ?? null);
  const [selectedNetworkOption, setSelectedNetworkOption] = useState<NetworkOption | null>(networkOptions[0] ?? null);

  const hourlyPrice = useMemo(() => {
    if (!selectedCPUOption || !selectedRAMOption || !selectedStorageOption) return 0;
    
    let basePrice = 
      cpu * Number(selectedCPUOption.price_per_core) +
      ram * Number(selectedRAMOption.price_per_gb) +
      disk * Number(selectedStorageOption.price_per_gb);
    
    // Traffic is charged per GB used (shown apart) when the panel has its price;
    // otherwise the option's monthly price per TB, spread over the hours.
    if (selectedNetworkOption && trafficPrice === null) {
      const networkHourlyCost = Number(selectedNetworkOption.price_per_tb) / HOURS_PER_MONTH;
      basePrice += networkHourlyCost;
    }
    
    return basePrice;
  }, [cpu, ram, disk, selectedCPUOption, selectedRAMOption, selectedStorageOption, selectedNetworkOption, trafficPrice]);

  // As the panel charges: by the hour, 730 hours a month, no monthly discount.
  const monthlyPrice = useMemo(() => hourlyPrice * HOURS_PER_MONTH, [hourlyPrice]);

  if (error) {
    return (
      <section className="py-28 bg-[#050B18] relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center">
            <p className="text-red-400">{t["configurator.error"]} {error}</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-28 bg-[#050B18] relative overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-30" />
      <div className="absolute top-1/4 right-0 w-80 h-80 bg-[rgba(0,212,255,0.06)] rounded-full blur-[100px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="section-label">{t["configurator.label"]}</span>
          <h2 className="font-display font-bold text-4xl lg:text-5xl text-white mt-3 mb-4">
            {t["configurator.title"]}{" "}
            <span className="gradient-text">{t["configurator.titleAccent"]}</span>
          </h2>
          <p className="text-[#8BA3CC] text-lg max-w-2xl mx-auto">
            {t["configurator.subtitle"]}
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left: Sliders */}
          <div className="lg:col-span-2 space-y-6">
            {/* CPU */}
            {selectedCPUOption && (
              <div className="glass rounded-2xl p-7 neon-border-blue">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[rgba(0,212,255,0.1)] flex items-center justify-center">
                      <Icon name="CpuChipIcon" size={20} className="text-[#00D4FF]" />
                    </div>
                    <div>
                      <div className="text-white font-semibold">{selectedCPUOption.name}</div>
                      <div className="text-[#4A6080] text-xs">
                        {selectedCPUOption.description || t["configurator.cpu.fallback"]}
                      </div>
                    </div>
                  </div>
                  <div className="text-2xl font-bold gradient-text">{cpu} ядра</div>
                </div>
                <input
                  type="range"
                  min={selectedCPUOption.min_cores}
                  max={selectedCPUOption.max_cores}
                  step={selectedCPUOption.step_cores}
                  value={cpu}
                  onChange={(e) => setCpu(Number(e.target.value))}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-[#4A6080] mt-2">
                  {[
                    selectedCPUOption.min_cores,
                    Math.floor(selectedCPUOption.max_cores * 0.25),
                    Math.floor(selectedCPUOption.max_cores * 0.5),
                    Math.floor(selectedCPUOption.max_cores * 0.75),
                    selectedCPUOption.max_cores,
                  ].map((v) => (
                    <span key={v} className={cpu === v ? "text-[#00D4FF]" : ""}>
                      {v}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* RAM */}
            {selectedRAMOption && (
              <div className="glass rounded-2xl p-7 neon-border-blue">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[rgba(79,124,255,0.1)] flex items-center justify-center">
                      <Icon name="CircleStackIcon" size={20} className="text-[#4F7CFF]" />
                    </div>
                    <div>
                      <div className="text-white font-semibold">{selectedRAMOption.name}</div>
                      <div className="text-[#4A6080] text-xs">
                        {selectedRAMOption.ram_type || t["configurator.ram.fallback"]}
                      </div>
                    </div>
                  </div>
                  <div className="text-2xl font-bold gradient-text">{ram} ГБ</div>
                </div>
                <input
                  type="range"
                  min={selectedRAMOption.min_gb}
                  max={selectedRAMOption.max_gb}
                  step={selectedRAMOption.step_gb}
                  value={ram}
                  onChange={(e) => setRam(Number(e.target.value))}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-[#4A6080] mt-2">
                  {[
                    selectedRAMOption.min_gb,
                    Math.floor(selectedRAMOption.max_gb * 0.25),
                    Math.floor(selectedRAMOption.max_gb * 0.5),
                    Math.floor(selectedRAMOption.max_gb * 0.75),
                    selectedRAMOption.max_gb,
                  ].map((v) => (
                    <span key={v} className={ram === v ? "text-[#4F7CFF]" : ""}>
                      {v}ГБ
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Disk */}
            {selectedStorageOption && (
              <div className="glass rounded-2xl p-7 neon-border-blue">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[rgba(124,58,237,0.1)] flex items-center justify-center">
                      <Icon name="ServerIcon" size={20} className="text-[#7C3AED]" />
                    </div>
                    <div>
                      <div className="text-white font-semibold">{selectedStorageOption.name}</div>
                      <div className="text-[#4A6080] text-xs">
                        {selectedStorageOption.description || t["configurator.disk.fallback"]}
                      </div>
                    </div>
                  </div>
                  <div className="text-2xl font-bold gradient-text">{disk} ГБ</div>
                </div>
                <input
                  type="range"
                  min={selectedStorageOption.min_size_gb}
                  max={selectedStorageOption.max_size_gb}
                  step={selectedStorageOption.step_size_gb}
                  value={disk}
                  onChange={(e) => setDisk(Number(e.target.value))}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-[#4A6080] mt-2">
                  {[
                    selectedStorageOption.min_size_gb,
                    Math.floor(selectedStorageOption.max_size_gb * 0.25),
                    Math.floor(selectedStorageOption.max_size_gb * 0.5),
                    Math.floor(selectedStorageOption.max_size_gb * 0.75),
                    selectedStorageOption.max_size_gb,
                  ].map((v) => (
                    <span key={v} className={disk === v ? "text-[#7C3AED]" : ""}>
                      {v}ГБ
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Network Bandwidth */}
            {networkOptions.length > 0 && (
              <div className="glass rounded-2xl p-7 border border-[rgba(79,124,255,0.12)]">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-lg bg-[rgba(16,185,129,0.1)] flex items-center justify-center">
                    <Icon name="SignalIcon" size={20} className="text-[#10B981]" />
                  </div>
                  <div>
                    <div className="text-white font-semibold">{t["configurator.network.title"]}</div>
                    <div className="text-[#4A6080] text-xs">{t["configurator.network.subtitle"]}</div>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {networkOptions.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => setSelectedNetworkOption(option)}
                      className={`p-4 rounded-xl text-left transition-all border ${
                        selectedNetworkOption?.id === option.id
                          ? "bg-[rgba(16,185,129,0.1)] border-[rgba(16,185,129,0.3)]"
                          : "border-[rgba(79,124,255,0.1)] hover:border-[rgba(79,124,255,0.3)] hover:bg-[rgba(79,124,255,0.05)]"
                      }`}
                    >
                      <div className="text-[#E8F0FF] font-medium mb-1">{option.name}</div>
                      {option.bandwidth_gbps && (
                        <div className="text-[#4A6080] text-xs mb-2">{option.bandwidth_gbps} Гбит/с</div>
                      )}
                      <div className="text-[#00D4FF] text-sm font-semibold">
                        {trafficPrice !== null
                          ? `${t["configurator.network.traffic"]} ${formatSom(trafficPrice)}/ГБ`
                          : `${Number(option.price_per_tb).toFixed(2)} сом/ТБ`}
                      </div>
                      {option.description && (
                        <div className="text-[#4A6080] text-xs mt-1">{option.description}</div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* OS Templates */}
            {osTemplates.length > 0 && (
              <div className="glass rounded-2xl p-7 border border-[rgba(79,124,255,0.12)]">
                <h3 className="text-white font-semibold mb-5">{t["configurator.os.title"]}</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {osTemplates.map((os, idx) => (
                    <button
                      key={os.id}
                      onClick={() => setSelectedOS(idx)}
                      className={`p-4 rounded-xl text-left transition-all flex items-center gap-3 ${
                        selectedOS === idx
                          ? "bg-[rgba(0,212,255,0.1)] border border-[rgba(0,212,255,0.3)]"
                          : "border border-[rgba(79,124,255,0.1)] hover:border-[rgba(79,124,255,0.3)] hover:bg-[rgba(79,124,255,0.05)]"
                      }`}
                    >
                      <span className="text-xl">{os.icon}</span>
                      <span className="text-sm text-[#E8F0FF] font-medium leading-tight">{os.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right: Price card */}
          <div className="lg:col-span-1">
            <div className="glass rounded-2xl p-7 neon-border sticky top-28">
              <h3 className="text-white font-bold text-xl mb-6">{t["configurator.summary.title"]}</h3>

              {/* Billing toggle */}
              <div className="flex gap-2 mb-6 p-1 bg-[rgba(79,124,255,0.06)] rounded-xl">
                {(["hourly", "monthly"] as const).map((b) => (
                  <button
                    key={b}
                    onClick={() => setBilling(b)}
                    className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-all ${
                      billing === b
                        ? "bg-[rgba(0,212,255,0.15)] text-[#00D4FF]"
                        : "text-[#4A6080] hover:text-[#8BA3CC]"
                    }`}
                  >
                    {b === "hourly" ? t["configurator.summary.hourly"] : t["configurator.summary.monthly"]}
                  </button>
                ))}
              </div>

              {/* Price display */}
              <div className="text-center py-6 border-b border-[rgba(79,124,255,0.12)] mb-6">
                <div className="text-5xl font-bold gradient-text">
                  {billing === "hourly" ? hourlyPrice.toFixed(4) : monthlyPrice.toFixed(2)} сом
                </div>
                <div className="text-[#4A6080] text-sm mt-2">
                  /{billing === "hourly" ? t["configurator.summary.perHour"] : t["configurator.summary.perMonth"]}
                </div>
                {trafficPrice !== null && (
                  <div className="text-xs text-[#4A6080] mt-1">{t["configurator.summary.traffic"]} {formatSom(trafficPrice)}/ГБ</div>
                )}
              </div>

              {/* Config summary */}
              <div className="space-y-3 mb-6 text-sm">
                {selectedCPUOption && (
                  <div className="flex items-center justify-between">
                    <span className="text-[#4A6080]">{t["configurator.summary.cpu"]}</span>
                    <div className="text-right">
                      <span className="text-[#E8F0FF] font-medium">{cpu} ядра</span>
                      <div className="text-[#4A6080] text-xs">
                        {(cpu * Number(selectedCPUOption.price_per_core)).toFixed(4)} сом/ч
                      </div>
                    </div>
                  </div>
                )}
                {selectedRAMOption && (
                  <div className="flex items-center justify-between">
                    <span className="text-[#4A6080]">{t["configurator.summary.ram"]}</span>
                    <div className="text-right">
                      <span className="text-[#E8F0FF] font-medium">{ram} ГБ</span>
                      <div className="text-[#4A6080] text-xs">
                        {(ram * Number(selectedRAMOption.price_per_gb)).toFixed(4)} сом/ч
                      </div>
                    </div>
                  </div>
                )}
                {selectedStorageOption && (
                  <div className="flex items-center justify-between">
                    <span className="text-[#4A6080]">{selectedStorageOption.name}</span>
                    <div className="text-right">
                      <span className="text-[#E8F0FF] font-medium">{disk} ГБ</span>
                      <div className="text-[#4A6080] text-xs">
                        {(disk * Number(selectedStorageOption.price_per_gb)).toFixed(4)} сом/ч
                      </div>
                    </div>
                  </div>
                )}
                {selectedNetworkOption && (
                  <div className="flex items-center justify-between">
                    <span className="text-[#4A6080]">{t["configurator.summary.network"]}</span>
                    <div className="text-right">
                      <span className="text-[#E8F0FF] font-medium">{selectedNetworkOption.name}</span>
                      <div className="text-[#4A6080] text-xs">
                        {trafficPrice !== null
                          ? `${formatSom(trafficPrice)}/ГБ трафика`
                          : `${(Number(selectedNetworkOption.price_per_tb) / HOURS_PER_MONTH).toFixed(4)} сом/ч`}
                      </div>
                    </div>
                  </div>
                )}
                {osTemplates.length > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-[#4A6080]">{t["configurator.summary.os"]}</span>
                    <div className="text-right">
                      <span className="text-[#E8F0FF] font-medium">{osTemplates[selectedOS]?.name}</span>
                      <div className="text-[#4A6080] text-xs">{t["configurator.summary.osPrice"]}</div>
                    </div>
                  </div>
                )}
              </div>

              <button className="w-full btn-primary py-4 rounded-xl font-bold text-sm mb-3">
                {t["configurator.summary.create"]} — {billing === "hourly" ? hourlyPrice.toFixed(4) : monthlyPrice.toFixed(2)} сом/{billing === "hourly" ? "ч" : "мес"}
              </button>
              <Link href={t["configurator.summary.all.href"]} className="block text-center text-xs text-[#4A6080] hover:text-[#8BA3CC] transition-colors">
                {t["configurator.summary.all.label"]}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}